import axios from 'axios'

// First-party reading analytics for the page turner viewer.
//
// One row per reading session is kept server-side, keyed by a client-generated
// session id and updated with monotonic maxima, so it is safe to beacon the
// running totals repeatedly (on open, on every page turn, on a heartbeat and
// when the tab is backgrounded/closed). Dropped or out-of-order beacons can
// never deflate the stored figures.

const VISITOR_KEY = 'pt_visitor_id'
const HEARTBEAT_MS = 15000
const MAX_DURATION_SECONDS = 86400 // matches the server-side sanity cap

function uuid () {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID()
  }

  // RFC4122 v4 fallback for older browsers.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// A stable id for the reader across visits. Falls back to an ephemeral id when
// storage is unavailable (private browsing / blocked), so the session still
// records — just without cross-visit linking.
function getVisitorId () {
  try {
    let id = window.localStorage.getItem(VISITOR_KEY)
    if (!id) {
      id = uuid()
      window.localStorage.setItem(VISITOR_KEY, id)
    }
    return id
  } catch (e) {
    return uuid()
  }
}

export default class ReadingTracker {
  // `useStoredId` controls cross-visit recognition. When false (reader declined
  // consent) we use a throwaway id that lives only for this session, so nothing
  // is stored on the device and repeat visits can't be linked.
  constructor (apiUrl, alias, totalPages, useStoredId = true) {
    this.endpoint = `${apiUrl}${alias}/events`
    this.sessionId = uuid()
    this.visitorId = useStoredId ? getVisitorId() : uuid()
    this.totalPages = totalPages || 0
    this.readSeconds = 0
    this.pagesViewed = new Set()
    this.maxPageReached = 0
    this.completed = false
    this.tickTimer = null
    this.heartbeatTimer = null
    this.boundFlush = this.flush.bind(this)
    this.boundVisibility = this.onVisibilityChange.bind(this)
  }

  // Begin tracking. `initialPages` is the set of page numbers visible on open.
  start (initialPages) {
    this.recordPages(initialPages)

    // Only count time while the tab is actually visible.
    this.tickTimer = setInterval(() => {
      if (document.visibilityState === 'visible' && this.readSeconds < MAX_DURATION_SECONDS) {
        this.readSeconds++
      }
    }, 1000)

    this.heartbeatTimer = setInterval(this.boundFlush, HEARTBEAT_MS)
    document.addEventListener('visibilitychange', this.boundVisibility)
    window.addEventListener('pagehide', this.boundFlush)

    this.send()
  }

  // Record a page turn and beacon the new totals.
  trackPages (pages) {
    this.recordPages(pages)
    this.send()
  }

  recordPages (pages) {
    const visible = pages || []
    visible.forEach((page) => {
      if (page >= 1 && page <= this.totalPages) {
        this.pagesViewed.add(page)
        if (page > this.maxPageReached) {
          this.maxPageReached = page
        }
      }
    })

    if (this.totalPages > 0 && this.maxPageReached >= this.totalPages) {
      this.completed = true
    }
  }

  onVisibilityChange () {
    if (document.visibilityState === 'hidden') {
      this.flush()
    }
  }

  payload () {
    return {
      session_id: this.sessionId,
      visitor_id: this.visitorId,
      duration_seconds: Math.min(this.readSeconds, MAX_DURATION_SECONDS),
      pages_viewed: this.pagesViewed.size,
      max_page_reached: this.maxPageReached,
      total_pages: this.totalPages,
      completed: this.completed
    }
  }

  // Normal beacon. Failures are ignored — analytics must never disrupt reading.
  send () {
    axios.post(this.endpoint, this.payload()).catch(() => {})
  }

  // Reliable best-effort beacon for backgrounding/unload, when an XHR may be
  // cancelled. sendBeacon survives the page going away.
  flush () {
    const data = JSON.stringify(this.payload())

    if (navigator.sendBeacon) {
      navigator.sendBeacon(this.endpoint, new Blob([data], { type: 'application/json' }))
      return
    }

    this.send()
  }

  stop () {
    clearInterval(this.tickTimer)
    clearInterval(this.heartbeatTimer)
    document.removeEventListener('visibilitychange', this.boundVisibility)
    window.removeEventListener('pagehide', this.boundFlush)
  }
}
